import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@xxtract/ui';

export interface MappingProfileResetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isResetting: boolean;
  onConfirm: (note: string) => void;
}

/**
 * h5-7 (FR35, EXPERIENCE.md Interaction Primitives): resetting a mapping profile is a destructive
 * action (it discards every remembered mapping) so it always goes through a confirmation dialog
 * with a summary — and, per the story, a MANDATORY audit note explaining who/why.
 */
export function MappingProfileResetDialog({ open, onOpenChange, isResetting, onConfirm }: MappingProfileResetDialogProps) {
  const { t } = useTranslation();
  const [note, setNote] = useState('');
  const noteId = useId();

  function handleConfirm() {
    if (note.trim() === '') return;
    onConfirm(note.trim());
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="mapping-profile-reset-dialog">
        <DialogHeader>
          <DialogTitle>{t('admin.mappingProfiles.resetDialog.title')}</DialogTitle>
          <DialogDescription>{t('admin.mappingProfiles.resetDialog.summary')}</DialogDescription>
        </DialogHeader>
        <label className="text-sm font-medium text-[var(--foreground)]" htmlFor={noteId}>
          {t('admin.mappingProfiles.resetDialog.noteLabel')}
        </label>
        <textarea
          id={noteId}
          data-testid="mapping-profile-reset-note"
          required
          value={note}
          onChange={(event) => setNote(event.target.value)}
          rows={3}
          className="rounded-[var(--radius-control)] border border-[var(--border)] bg-[var(--background)] p-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--secondary)]"
        />
        <DialogFooter>
          <DialogClose>
            <Button variant="outline" data-testid="mapping-profile-reset-cancel">{t('common.cancel')}</Button>
          </DialogClose>
          <Button
            variant="destructive"
            data-testid="mapping-profile-reset-confirm"
            disabled={isResetting || note.trim() === ''}
            onClick={handleConfirm}
          >
            {t('admin.mappingProfiles.resetDialog.confirmCta')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
